// ============================================================================
// 📦 VERSION: 2025.11.27.1700
// ACHIEVEMENT SYSTEM - 16-Tier Addiction-Optimised Progression
// 🔧 FIX 2025.11.27.1700: Expanded from 12 to 16 tiers
// 🔧 FIX 2025.11.27.1700: Front-loaded early rewards for player retention
// 🔧 FIX 2025.11.27.1700: Added motivational taglines for each achievement
// ============================================================================

export const ACHIEVEMENT_MILESTONES = [
  // Early Game - The Hook (First Session)
  { traks: 50, icon: '⭐', name: 'Rising Star', bonusTraks: 5, tagline: 'Your journey begins!' },
  { traks: 150, icon: '🥉', name: 'Bronze Medal', bonusTraks: 10, tagline: 'First medal earned!' },
  { traks: 300, icon: '🎖️', name: 'Bronze Elite', bonusTraks: 15, tagline: 'Above the rest!' },
  { traks: 500, icon: '🥈', name: 'Silver Medal', bonusTraks: 25, tagline: 'Rising through the ranks!' },
  
  // Mid Game - The Line (Multi-Session)
  { traks: 800, icon: '🏵️', name: 'Silver Elite', bonusTraks: 35, tagline: 'Shining bright!' },
  { traks: 1200, icon: '🥇', name: 'Gold Medal', bonusTraks: 50, tagline: 'Champion material!' },
  { traks: 1800, icon: '✨', name: 'Gold Elite', bonusTraks: 65, tagline: 'Pure gold!' },
  { traks: 2500, icon: '🏅', name: 'Platinum Medal', bonusTraks: 80, tagline: 'Platinum status!' },
  
  // Late Game - The Sinker (Long-term)
  { traks: 4000, icon: '💚', name: 'Emerald Medal', bonusTraks: 100, tagline: 'Rare and precious!' },
  { traks: 6000, icon: '💙', name: 'Sapphire Medal', bonusTraks: 150, tagline: 'True gem!' },
  { traks: 10000, icon: '🏆', name: 'Ruby Cup', bonusTraks: 200, tagline: 'Welcome to the elite!' },
  { traks: 15000, icon: '💜', name: 'Amethyst Cup', bonusTraks: 275, tagline: 'Serious competitor!' },
  
  // Elite Game - Ultimate Goals
  { traks: 25000, icon: '🟡', name: 'Topaz Cup', bonusTraks: 350, tagline: 'Veteran player!' },
  { traks: 40000, icon: '⚫', name: 'Obsidian Cup', bonusTraks: 450, tagline: 'Dark horse champion!' },
  { traks: 60000, icon: '💎', name: 'Diamond Cup', bonusTraks: 600, tagline: 'Legendary status!' },
  { traks: 100000, icon: '👑', name: 'The Crown', bonusTraks: 1000, tagline: 'You are LEGENDARY!' }
];

// Get current achievement based on total TRAKs
export const getCurrentAchievement = (totalTraks) => {
  for (let i = ACHIEVEMENT_MILESTONES.length - 1; i >= 0; i--) {
    if (totalTraks >= ACHIEVEMENT_MILESTONES[i].traks) {
      return ACHIEVEMENT_MILESTONES[i];
    }
  }
  return { icon: '🌱', name: 'Newcomer', traks: 0, bonusTraks: 0, tagline: 'Welcome to TRAK!' }; // Default for < 50 TRAKs
};

// Get next achievement milestone
export const getNextAchievement = (totalTraks) => {
  for (let i = 0; i < ACHIEVEMENT_MILESTONES.length; i++) {
    if (totalTraks < ACHIEVEMENT_MILESTONES[i].traks) {
      return ACHIEVEMENT_MILESTONES[i];
    }
  }
  return null; // Already at max (The Crown)
};

// Calculate progress to next achievement (for internal use only - NOT shown to player)
export const getAchievementProgress = (totalTraks) => {
  const current = getCurrentAchievement(totalTraks);
  const next = getNextAchievement(totalTraks);
  
  if (!next) {
    return { current: totalTraks, needed: 0, percentage: 100, isMaxLevel: true }; // The Crown achieved
  }
  
  const previousThreshold = current.traks || 0;
  const progress = totalTraks - previousThreshold;
  const needed = next.traks - previousThreshold;
  const percentage = Math.floor((progress / needed) * 100);
  
  return { current: progress, needed, percentage, isMaxLevel: false };
};

// Check if a new milestone was just reached (for triggering celebration)
// Returns the milestone object if just reached, null otherwise
export const checkMilestoneReached = (previousTraks, newTraks, claimedMilestones = []) => {
  for (let i = 0; i < ACHIEVEMENT_MILESTONES.length; i++) {
    const milestone = ACHIEVEMENT_MILESTONES[i];
    
    // Check if we crossed this threshold AND haven't claimed it yet
    if (previousTraks < milestone.traks && 
        newTraks >= milestone.traks && 
        !claimedMilestones.includes(milestone.traks)) {
      return milestone;
    }
  }
  return null; // No new milestone reached
};

// Get all milestones that should be claimed (for catching up if any were missed)
export const getUnclaimedMilestones = (totalTraks, claimedMilestones = []) => {
  const unclaimed = [];
  
  for (let i = 0; i < ACHIEVEMENT_MILESTONES.length; i++) {
    const milestone = ACHIEVEMENT_MILESTONES[i];
    
    if (totalTraks >= milestone.traks && !claimedMilestones.includes(milestone.traks)) {
      unclaimed.push(milestone);
    }
  }
  
  return unclaimed;
};
